<template>
   <div class='energy-base-line'>
      <process-header :step="3"/>
      <div class="body">
          <common-form
            class='plan-form'
            form-title="Develop Energy Baseline"
            :back-route="{name: 'energy-sub-commitee'}"
            :errors="errors.non_field_errors"
            @submit="save"
            @save-and-continue="saveAndContinue"
            >
            <input-field
               v-model="showRadio"
               type="checkbox"
               label="Base line survey"
               :options="baselineOptions"
               label50
               one-line
            />
            <input-field
               class='baseline-radio'
               v-if="showRadio"
               v-model="form.baseline_survey"
               type="radio"
               label="Select data collection approach"
               :options="baselineOptions"
            />
            <div
               v-if="showRadio && form.baseline_survey==='HHS'"
               >
               <span class='note'>Note: *Try to make sure there is at least one cluster per ward</span>
               <div class='btn-group'>
                  <atom-button :disabled="sst_is_locked" @click='doSST'>
                     <img v-if="sst_is_locked" src="../../../../../assets/icons/lock.svg"/>
                     Generate SST
                  </atom-button>
                  <atom-button @click='lockSSTProject' color="light">
                     {{ sst_is_locked ? 'Unlock SST' : 'Lock SST'}}
                  </atom-button>
                  <atom-button @click='showParamsForm=true' color="light">
                     View/Edit Parameters
                  </atom-button>
               </div>
            </div>
            <input-field
               v-model="form.infrastructure_survey"
               type="checkbox"
               label="Infrastructure Survey"
               :options="baselineOptions"
               label50
               one-line
            />
            <input-field
               v-model="form.institution_enterprise_survey"
               type="checkbox"
               label="Institution/Enterprise Survey"
               :options="baselineOptions"
               label50
               one-line
            />
          </common-form>
      </div>
      <sst-params-form
         :visible='showParamsForm'
         :parent-params="paramsForm"
         @submit='handleParamtersSubmit'
         @close='showParamsForm=false'
      />
   </div>
</template>

<script>
import ProcessHeader from "../Header";
import CommonForm from '@/components/organisms/Home/Plan/Form';
import SstParamsForm from '@/components/organisms/Home/MepToolMap/SSTParamsForm';
import InputField from '@/components/molecules/Login/InputField';
import AtomButton from '@/components/atoms/Login/Button';
import { fetchWithAuthToken, postWithAuthToken } from '../../../../../axios';
import { mapGetters,mapActions } from 'vuex';
import { getNavigator } from '../../../../../js/mapNavigator';
import { confirm, hideLoading, showError, showLoading, showSuccess } from '../../../../../js/ui-helpers';
import { getLayerById } from '../../../../../js/layers';
import { geojsonFormat } from '../../../../../js/tools/tooltip';
import { getMapAsync } from '../../../../../js/map';

export default {
   name: "energy-baseline",
   components: {
      ProcessHeader,
      CommonForm, 
      InputField,
      AtomButton,
      SstParamsForm,
   },
   data()
   {
      return {
         approach: '',
         form: {
            baseline_type: null,
            infrastructure_survey: false,
            institution_enterprise_survey: false,
         },
         showParamsForm: false,
         paramsForm: {
            hh_per_municipality: 400,
            hh_per_cluster_urban: 10,
            hh_per_cluster_rural: 10,
            desired_cluster_size: 5,
         },
         planSstResults: {

         },
         sst_is_locked: true,
         errors: {
            non_field_errors: []
         },
         showRadio: false,
         baselineOptions: [
            {title: 'Household Survey', value: 'HHS'},
            {title: 'Focus Group Discussion/Ward Survey', value: 'FGD'},
         ],
      };
   },
   computed: {
      ...mapGetters('plan', ['getPlan', ]),
      planId()
      {
         return this.$route.params.id;
      },
   },
   async created()
   {
      showLoading();
      const {data} = await fetchWithAuthToken(`api/plan/${this.planId}/survey-options`);
      this.form = data;
      this.showRadio = Boolean(data.baseline_survey);
      this.planSstResults = await this.fetchSSTProject({plan_id: this.planId});
      this.setSstResultsToShow(this.planSstResults);
      this.updateSSTClusterFeatures(this.planSstResults.cluster_geojson);
      this.sst_is_locked = this.planSstResults && this.planSstResults.locked;
      hideLoading();
   },
   beforeDestroy()
   {
      this.setSstResultsToShow(null);
   },
   mounted()
   {
      this.updatePlanLayerVisibilityFor({sst: true});
   },
   methods: {
      ...mapActions('tools', ['performSstAnalysis',]),
      ...mapActions('layers', ['updatePlanLayerVisibilityFor',]),
      ...mapActions('plan', ['fetchSSTProject', 'setSstResultsToShow']),
      handleParamtersSubmit(sstParams)
      {
         this.showParamsForm = false;
         this.paramsForm = sstParams;
      },
      updateSSTClusterFeatures(geojson)
      {
         getMapAsync().then(map =>
         {
            const layer = getLayerById('plan_sst_clusters', map);
            const source = layer.getSource();
            source.clear();
            geojson && source.addFeatures(geojsonFormat.readFeatures(geojson));
         });
      },
      async selectProjectMunicipality(navigator)
      {
         const {sddmm} = this.getPlan.municipality;
         const scode = parseInt(sddmm.toString()[0]);
         const dcode = parseInt(sddmm.toString().slice(1,3));
         await navigator.state.select(scode);
         await navigator.district.select(dcode);
         await navigator.municipality.select(parseInt(sddmm))
      },
      async doSST()
      {
         const {sddmm} = this.getPlan.municipality;
         await this.selectProjectMunicipality(getNavigator())
         const {data,success}  = await this.performSstAnalysis({sddmm, ...this.paramsForm, isPlan: true});
         if (success)
         {
            this.planSstResults = data;
            this.updateSSTClusterFeatures(data.cluster_geojson)
            this.setSstResultsToShow(data);
         }
      },
      resetErrors()
      {
         this.errors.non_field_errors = [];
      },
      checkErrors()
      {
         const errors = [];
         if (
            this.form.institution===false &&
            this.form.infrastructure===false &&
            (this.showRadio===false || this.form.baseline_type==='')
         )
         {
            errors.push("You have to select at least one option");
         }
         if (this.sst_is_locked===true && (this.planSstResults==null || (this.planSstResults && this.planSstResults.cluster_geojson==null)))
         {
            errors.push("You can't lock sst tool before running it");
         }
         return errors;
      },
      lockSSTProject()
      {
         confirm({
            title: "Lock Spatial Sampling",
            msg: `Are you sure you want to ${this.sst_is_locked ? 'unlock' : 'lock'} spatial sampling`,
            onSuccess: () => this.sst_is_locked = !this.sst_is_locked,
         });
      },
      save({goToNext=false})
      {
         this.resetErrors();
         const errors = this.checkErrors();
         if (errors.length)
         {
            setTimeout(() => this.errors.non_field_errors = errors, 100);
            return;
         }
         Promise.all([
            postWithAuthToken(`api/plan/${this.planId}/survey-options`, this.form, {method: 'put'}),
            this.planSstResults && postWithAuthToken(`api/sst/${this.planId}`, {...this.planSstResults,locked: this.sst_is_locked }, {method: 'put'}),
         ])
         .then(() => 
         {
            showSuccess('Energy Baseline', 'Sucessfully saved your choices');
            if (goToNext) 
               this.$router.push({name: 'energy-baseline-bat'});
         })
         .catch(() => showError('Energy Baseline', 'Could save the changes'));
      },
      saveAndContinue()
      {
         this.save({goToNext: true});
      }
   },
};
</script>

<style lang="scss" scoped>
.energy-base-line {
   font-family: $primary-font;
   color: $font-color;
   > .body {
      padding: 0.6em;
   }
   .step-title {
      span {
         font-family: $secondary-font;
         font-weight: 500;
         font-size: 1.05em;
      }
      font-family: inherit;
      display: flex;
      justify-content: space-between;
   }
   .baseline-radio {
      margin-top: -0.9em;
      padding-left: 1em;
   }
   .note {
      font-size: 0.8em;
   }

   .btn-group {
      display: flex;
      margin: 0 0.3em;
      > * {
         margin-right: 0.3em;
         a {
            color:inherit;
         }
         &:last-child {
            margin-right: 0;
         }
      }
   }
}
</style>
